import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  useMediaQuery,
  Chip,
  Switch,
} from '@mui/material';
import { CreateGameConfig } from '@par/shared/types/game';
import { WhosOnline } from '../room/types';
import {
  uniqueNamesGenerator,
  adjectives,
  animals,
} from 'unique-names-generator';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/system';
import { fetchGet } from '../../api/common';
import { Profile } from '@par/shared/types/player';
import { LensBlur, SelfImprovement } from '@mui/icons-material';
import { Diversity3 } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import JoinLeftIcon from '@mui/icons-material/JoinLeft';
import { ValorantButton } from '../create-game/ValorantButton';
import './CloseButtonAnimation.scss';
import { PlayMenuTabsSolo } from './PlayMenuTabsSolo';
import { PlayMenuTabsMultiplayer } from './PlayMenuTabsMultiplayer';
import { PlayMenuTabsAsync1v1 } from './PlayMenuTabsAsync1v1';
import { Mode } from '@par/shared/common/types';
import GroupsIcon from '@mui/icons-material/Groups';
import { ToggleCasualPro } from './ToggleCasualPro';
import pluralize from 'pluralize';

export type GameType = 'casual' | 'pro';
export type GameVariant = 'normal' | 'survival' | 'starter-kit';
export type SurvivalMode = 'endurance' | 'challenge' | 'demon';

const MAX_DEMO_SETS = 5;
const MMR_SEARCH_RANGE_DELTA = 100;

const nameGenConfig = {
  dictionaries: [adjectives, animals],
  separator: ' ',
  length: 2,
};

type PropTypes = {
  connected?: boolean;
  demoCount: number;
  closePlayMenu: () => void;
  disablePlayButton: boolean;
  enabledTeams: boolean;
  onCategoryBanClick: () => void;
  onlinePlayer?: WhosOnline;
  onPlayClick: (onlinePlayer?: WhosOnline) => void;
  onViewHandicapWinRatesClick: () => void;
  onViewSearchPoolClick: (profileId: string) => void;
  openPlayMenu: boolean;
  profile?: Profile;
  setCreateGameConfig: (opts: CreateGameConfig) => void;
};

export const PlayMenu2 = ({
  connected,
  demoCount,
  closePlayMenu,
  disablePlayButton,
  enabledTeams,
  onCategoryBanClick,
  onlinePlayer,
  onPlayClick,
  onViewHandicapWinRatesClick,
  onViewSearchPoolClick,
  openPlayMenu,
  profile,
  setCreateGameConfig,
}: PropTypes) => {
  const theme = useTheme();
  const mdHeightScreen = useMediaQuery('(max-height:900px)');
  const [customGameName, setCustomGameName] = useState('');
  const [teamUsernames, setTeamUsernames] = useState<[string, string]>();
  const [categorySelections, setCategorySelections] = useState(
    profile?.recentPlayMenuSettings?.categorySelections || []
  );
  const [categorySet, setCategorySet] = useState(
    profile?.recentPlayMenuSettings?.category || 'all'
  );
  const [gameType, setGameType] = useState<GameType>(
    profile?.recentPlayMenuSettings?.difficulty || 'casual'
  );
  const [survivalMode, setSurvivalMode] = useState<SurvivalMode>(
    profile?.recentPlayMenuSettings?.survivalMode || 'challenge'
  );
  const [gameVariant, setGameVariant] = useState<GameVariant>(
    profile?.recentPlayMenuSettings?.variant || 'normal'
  );
  const [matchMaking, setMatchMaking] = useState(
    profile?.recentPlayMenuSettings?.matchMaking || false
  );
  const [mode, setMode] = useState<Mode>(
    profile?.recentPlayMenuSettings?.mode || 'solo'
  );
  const [ranked, setRanked] = useState(
    profile?.recentPlayMenuSettings?.ranked || false
  );
  const [demo, setDemo] = useState(false);
  const [allowSpectators, setAllowSpectators] = useState(
    profile?.recentPlayMenuSettings?.allowSpectators || false
  );
  const [soloMultiAsync, setSoloMultiAsync] = useState<
    'solo' | 'multiplayer' | 'teams' | 'async'
  >(profile?.recentPlayMenuSettings?.soloMultiAsync || 'solo');
  // const [casualPro, setCasualPro] = useState<'casual' | 'pro'>('casual');
  const [searchOptions, setSearchOptions] = useState<'mmr' | 'handicap'>('mmr');
  const [expandedSearchRange, setExpandedSearchRange] = useState<number[]>([
    profile.rankedRating - MMR_SEARCH_RANGE_DELTA,
    profile.rankedRating + MMR_SEARCH_RANGE_DELTA,
  ]);
  const [matchesAvailable, setMatchesAvailable] = useState<number>();
  const [seedMatch, setSeedMatch] = useState(
    profile?.recentPlayMenuSettings?.voluntarySeedMatch
  );
  const [seededMatches, setSeededMatches] = useState<number>();
  const [playClicked, setPlayClicked] = useState(false);
  const [league, setLeague] = useState(false);

  const expandSearchMarks = [
    {
      value: profile.rankedRating - 250,
      label: `${profile.rankedRating - 250}`,
    },

    {
      value: profile.rankedRating,
      label: `${profile.rankedRating}`,
    },

    {
      value: profile.rankedRating + 250,
      label: `${profile.rankedRating + 250}`,
    },
  ];

  useEffect(() => {
    const playMenuOps: CreateGameConfig = {
      allowSpectators: allowSpectators,
      customGameName: customGameName,
      demons: false,
      gameType: gameType,
      categorySelections: categorySelections,
      categorySet: categorySet,
      matchMaking: matchMaking,
      mode: mode,
      customSitAndGoMaxPlayers: 10,
      ranked: ranked,
      demo: demo,
      searchOptions: searchOptions,
      soloMultiAsync,
      starterKit: null,
      tournament: null,
      league: false,
    };
    setCreateGameConfig(playMenuOps);
    const generatedcustomGameName = uniqueNamesGenerator({
      ...nameGenConfig,
      style: 'capital',
    });
    setCustomGameName(generatedcustomGameName);
    fetchAvailableMatches(searchOptions);
    const teamUsername1 = uniqueNamesGenerator({
      ...nameGenConfig,
      style: 'capital',
    });
    const teamUsername2 = uniqueNamesGenerator({
      ...nameGenConfig,
      style: 'capital',
    });
    setTeamUsernames([pluralize(teamUsername1), pluralize(teamUsername2)]);
  }, [openPlayMenu]);

  useEffect(() => {
    const newOpts: CreateGameConfig = {
      allowSpectators: allowSpectators,
      categorySelections: categorySelections,
      categorySet: categorySet,
      createEmptyGame: false,
      customGameName: customGameName,
      customSitAndGoMaxPlayers: 10,
      demo: demo,
      demons: false,
      expandedSearchRange:
        profile.rankedRating - expandedSearchRange[0] - MMR_SEARCH_RANGE_DELTA,
      gameType: gameType,
      league: league,
      matchMaking: matchMaking,
      mode: mode,
      ranked: ranked,
      searchOptions: searchOptions,
      soloMultiAsync: soloMultiAsync,
      starterKit: null,
      survivalMode: survivalMode,
      teamUsernames,
      tournament: null,
      variant: gameVariant,
      voluntarySeedMatch: seedMatch,
    };
    setCreateGameConfig(newOpts);
  }, [
    allowSpectators,
    categorySelections,
    categorySet,
    customGameName,
    demo,
    expandedSearchRange,
    gameType,
    gameVariant,
    league,
    matchMaking,
    mode,
    ranked,
    seedMatch,
    searchOptions,
    soloMultiAsync,
    survivalMode,
  ]);

  useEffect(() => {
    if (!expandedSearchRange) return;
    fetchAvailableMatches(searchOptions);
  }, [expandedSearchRange]);

  useEffect(() => {
    fetchAvailableMatches(searchOptions);
  }, [searchOptions]);

  useEffect(() => {
    if (demoCount >= MAX_DEMO_SETS) {
      setDemo(false);
    }
  }, [demoCount]);

  useEffect(() => {
    if (!openPlayMenu) {
      setPlayClicked(false);
    }
  }, [openPlayMenu]);

  const fetchAvailableMatches = async (searchOptions: 'mmr' | 'handicap') => {
    if (searchOptions === 'mmr') {
      const res = await fetchGet(
        `/match-making/available-matches/range/${profile.id}/${
          profile.rankedRating - expandedSearchRange[0] - MMR_SEARCH_RANGE_DELTA
        }`
      );
      setMatchesAvailable(res.matchesAvailable);
      setSeededMatches(res.seededMatches);
    } else {
      const res = await fetchGet(
        `/match-making/available-matches/all/${profile.id}`
      );
      setMatchesAvailable(res.matchesAvailable);
    }
  };

  const handleSoloMultiChange = (
    event: React.MouseEvent<HTMLElement>,
    value: 'solo' | 'multiplayer' | 'teams' | 'async'
  ) => {
    if (value === null) return;
    setSoloMultiAsync(value);
    if (value === 'async') {
      setMatchMaking(true);
      setMode('match');
      setRanked(true);
      setDemo(false);
      setCategorySet('all');
      setGameType('pro');
      setGameVariant('normal');
      setSurvivalMode('challenge');
    } else if (value === 'multiplayer') {
      setMatchMaking(false);
      setMode('sit-n-go');
    } else if (value === 'teams') {
      setMatchMaking(false);
      setRanked(false);
      setMode('teams');
      setCategorySet('all');
    } else {
      setMatchMaking(false);
      setRanked(false);
      setDemo(false);
      setMode('solo');
      setGameVariant('normal');
    }
  };

  const handleCasualProChange = (event: any, value: 'casual' | 'pro') => {
    if (value === null) return;
    setGameType(value);
  };

  const handleGameVariantChange = (
    event: any,
    variant: 'normal' | 'survival'
  ) => {
    setGameVariant(variant);
  };

  const handleSurvivalModeChange = (
    event: React.MouseEvent<HTMLElement>,
    value: 'challenge' | 'endurance'
  ) => {
    if (value === null) return;
    setSurvivalMode(value);
  };

  const handleMultiplayerMode = (
    event: React.MouseEvent<HTMLElement>,
    value: 'friendly' | 'ranked'
  ) => {
    if (value === null) return;
    if (value === 'friendly') {
      setMatchMaking(false);
      setRanked(false);
      setDemo(false);
      setGameType('casual');
      setGameVariant('normal');
      setSurvivalMode('challenge');
    } else {
      setMatchMaking(false);
      setRanked(true);
      setDemo(false);
      setCategorySet('all');
      setGameType('pro');
      setGameVariant('normal');
      setSurvivalMode('challenge');
    }
  };

  const handleSearchOption = (
    event: React.MouseEvent<HTMLElement>,
    value: 'mmr' | 'handicap'
  ) => {
    if (value === null) return;
    setSearchOptions(value);
  };

  const qualifiesForPunchcardPlay = (topCategory: string) => {
    if (!topCategory) {
      setCategorySet('none');
    } else {
      setCategorySet(topCategory);
    }
  };

  const updatedCheckedCategories = (categories: any) => {
    // console.log('updatedCheckedCategories', categories);
    const catSelections = Object.keys(categories).filter(
      (key) => categories[key]
    );
    setCategorySelections(catSelections);
  };

  const updatedCheckedTopCategories = (topCategories: any) => {
    // console.log('updatedCheckedTopCategories', topCategories);
  };

  const handleSearchSliderChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (activeThumb === 0) {
      if (newValue[0] > profile.rankedRating - MMR_SEARCH_RANGE_DELTA) return;
      setExpandedSearchRange([
        newValue[0],
        profile.rankedRating + (profile.rankedRating - newValue[0]),
      ]);
    } else {
      if (newValue[1] < profile.rankedRating + MMR_SEARCH_RANGE_DELTA) return;
      setExpandedSearchRange([
        profile.rankedRating - (newValue[1] - profile.rankedRating),
        newValue[1],
      ]);
    }
  };

  const CreateGameTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#111a19',
      color: 'white',
      maxWidth: 400,
      borderRadius: 5,
      border: '1px solid',
      borderColor: '#a48672',
    },
  }));

  const createGameTooltip = (
    <>
      <Stack>
        <Typography>
          A game will be created in the lobby. Start the game once enough
          players have joined.
        </Typography>
      </Stack>
    </>
  );

  const teamGameTooltip = (
    <>
      <Stack>
        <Typography>
          A game will be created in the lobby and you will be the host. Start
          the game once everyone is ready.
        </Typography>
      </Stack>
    </>
  );

  const selectCategoryTooltip = (
    <>
      <Stack>
        <Typography>
          You must select at least one category to start a game
        </Typography>
      </Stack>
    </>
  );

  // const disableButton = () =>
  //   !connected ||
  //   (soloMultiAsync === 'solo' &&
  //     gameVariant === 'normal' &&
  //     !categorySelections.length &&
  //     categorySet === 'none') ||
  //   (soloMultiAsync === 'multiplayer' &&
  //     !ranked &&
  //     !categorySelections.length &&
  //     categorySet === 'none');

  const handlePlayClick = (onlinePlayer: WhosOnline) => {
    setPlayClicked(true);
    onPlayClick(onlinePlayer);
  };

  return (
    <Drawer
      anchor="right"
      open={openPlayMenu}
      onClose={closePlayMenu}
      PaperProps={{
        sx: {
          marginTop: '74px',
          backgroundColor: '#111a19',
          backgroundImage: 'none',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        }}
      >
        <Stack spacing={mdHeightScreen ? 2 : 5} sx={{ width: '1000px' }}>
          <ToggleButtonGroup
            color="primary"
            value={soloMultiAsync}
            exclusive
            onChange={handleSoloMultiChange}
            sx={{
              height: '120px',
              width: '100%',
            }}
          >
            <ToggleButton
              value="solo"
              sx={{ height: '100%', width: '100%', borderBottomLeftRadius: 0 }}
              fullWidth
            >
              <Stack
                width={'50%'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <SelfImprovement
                  sx={{
                    width: '40%',
                    height: '40%',
                    color:
                      soloMultiAsync !== 'solo'
                        ? theme.palette.grey[300]
                        : null,
                  }}
                />
                <Typography
                  variant={soloMultiAsync === 'solo' ? 'h5led' : 'h5'}
                  textAlign={'center'}
                  color={
                    soloMultiAsync !== 'solo' ? theme.palette.grey[400] : null
                  }
                >
                  SOLO
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton
              value="multiplayer"
              sx={{ height: '100%', width: '100%' }}
              fullWidth
            >
              <Stack
                width={'50%'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <GroupsIcon
                  sx={{
                    width: '40%',
                    height: '40%',
                    color:
                      soloMultiAsync !== 'solo'
                        ? theme.palette.grey[300]
                        : null,
                  }}
                />
                <Typography
                  variant={soloMultiAsync === 'multiplayer' ? 'h5led' : 'h5'}
                  color={
                    soloMultiAsync !== 'multiplayer'
                      ? theme.palette.grey[300]
                      : null
                  }
                  textAlign={'center'}
                  whiteSpace={'pre'}
                >
                  SIT & GO
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton
              value="teams"
              sx={{ height: '100%', width: '100%' }}
              fullWidth
            >
              <Chip
                variant="outlined"
                label="Beta"
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 40,
                  color: theme.palette.dailies.main,
                  borderColor: theme.palette.dailies.main,
                }}
              />
              <Stack
                width={'50%'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Diversity3
                  sx={{
                    width: '40%',
                    height: '40%',
                    color:
                      soloMultiAsync !== 'solo'
                        ? theme.palette.grey[300]
                        : null,
                  }}
                />
                <Typography
                  variant={soloMultiAsync === 'teams' ? 'h5led' : 'h5'}
                  color={
                    soloMultiAsync !== 'teams' ? theme.palette.grey[300] : null
                  }
                  textAlign={'center'}
                >
                  Teams
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton
              value="async"
              sx={{ height: '100%', width: '100%', borderBottomRightRadius: 0 }}
              fullWidth
            >
              <Stack
                width={'50%'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <JoinLeftIcon
                  sx={{
                    width: '40%',
                    height: '40%',
                    color:
                      soloMultiAsync !== 'solo'
                        ? theme.palette.grey[300]
                        : null,
                  }}
                />
                <Typography
                  variant={soloMultiAsync === 'async' ? 'h5led' : 'h5'}
                  color={
                    soloMultiAsync !== 'async' ? theme.palette.grey[300] : null
                  }
                  textAlign={'center'}
                  whiteSpace={'pre'}
                >
                  ASYNC 1v1
                </Typography>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
          {soloMultiAsync === 'solo' ? (
            <PlayMenuTabsSolo
              gameType={gameType}
              gameVariant={gameVariant}
              handleCasualProChange={handleCasualProChange}
              handleGameVariantChange={handleGameVariantChange}
              handleSurvivalModeChange={handleSurvivalModeChange}
              qualifiesForPunchcardPlay={qualifiesForPunchcardPlay}
              profile={profile}
              survivalMode={survivalMode}
              updatedCheckedCategories={updatedCheckedCategories}
              updatedCheckedTopCategories={updatedCheckedTopCategories}
            />
          ) : soloMultiAsync === 'multiplayer' ? (
            <>
              <PlayMenuTabsMultiplayer
                gameType={gameType}
                handleCasualProChange={handleCasualProChange}
                handleMultiplayerMode={handleMultiplayerMode}
                onCategoryBanClick={onCategoryBanClick}
                profile={profile}
                qualifiesForPunchcardPlay={qualifiesForPunchcardPlay}
                ranked={ranked}
                updatedCheckedCategories={updatedCheckedCategories}
                updatedCheckedTopCategories={updatedCheckedTopCategories}
              />
              <Typography textAlign={'center'}>
                Note: "Sit & Go" games are live and start when enough players
                are online and ready to play
              </Typography>
            </>
          ) : soloMultiAsync === 'teams' ? (
            <>
              <ToggleCasualPro
                gameType={gameType}
                handleCasualProChange={handleCasualProChange}
              />
              <Stack spacing={4}>
                <Stack
                  direction={'row'}
                  spacing={2}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <Typography>Status:</Typography>
                  <Typography color={enabledTeams ? 'success.main' : 'error'}>
                    {enabledTeams ? 'Available' : 'Unavailable'}
                  </Typography>
                </Stack>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <LensBlur htmlColor={'#dba5ff'} />
                  <Stack direction={'row'} spacing={1}>
                    <Typography>TEAMS mode is available from</Typography>
                    <Typography fontWeight={600}>6pm-10pm PT</Typography>
                  </Stack>
                  <LensBlur htmlColor={'#dba5ff'} />
                </Stack>
                {/* {profile.admin ? (
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <Typography variant="body1">Admin: League</Typography>
                    <Switch
                      checked={league}
                      onChange={(e) => setLeague(e.target.checked)}
                    />
                  </Stack>
                ) : null} */}
              </Stack>
            </>
          ) : (
            <Stack spacing={mdHeightScreen ? 3 : 5} mt={'0px !important'}>
              {/* <StyledPaperItem sx={{ width: '80%' }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    width={'100%'}
                    textAlign={'center'}
                    fontWeight={600}
                    sx={{ color: theme.palette.info.main }}
                  >
                    What are Asynchronous 1v1s?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={3}>
                    <Typography>
                      Players can play a game that will upload into a pool of
                      games. Others can then pull from this pool and play
                      asynchronously.
                    </Typography>
                    <Typography>
                      We'll try to match you up against someone near your skill
                      level for the best possible experience.
                    </Typography>
                    <Typography>
                      For every game you play from "Search", you need to
                      contribute one of your own, called a "Seed", to replenish
                      the pool.
                    </Typography>
                    <Typography>
                      All searched games are Ranked (Pro difficulty in all
                      categories)
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </StyledPaperItem> */}

              {/* <Typography
                textAlign={'center'}
                mt={'0px !important'}
                py={2}
                sx={{ backgroundColor: '#f9f9f90d' }}
              >
                Search credits left: {profile.seedMatch || 0}
              </Typography> */}
              {/* <Typography variant="h6" textAlign={'center'} fontWeight={600}>
                Search credits left: {profile.seedMatch || 0}
              </Typography> */}
              <PlayMenuTabsAsync1v1
                expandedSearchRange={expandedSearchRange}
                matchesAvailable={matchesAvailable}
                profile={profile}
                onViewHandicapWinRatesClick={onViewHandicapWinRatesClick}
                handleSearchOption={handleSearchOption}
                handleSearchSliderChange={handleSearchSliderChange}
              />
              {profile.seedMatch === 0 ? (
                <Box width={'100%'} display={'flex'} justifyContent={'center'}>
                  <Stack width={'50%'}>
                    <Typography textAlign={'center'}>
                      For Search play, you must alternate between seeding a
                      match and playing a match. This will help sustain a
                      healthy volume of games in the pool.
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <>
                  {/* <Typography
                    variant="h6"
                    display="inline"
                    sx={{
                      textAlign: 'center',
                      marginLeft: '4px',
                      background: matchesAvailable
                        ? 'linear-gradient(106deg, rgb(30 30 30) 0%, rgb(29 146 4) 50%, rgb(30 30 30) 100%)'
                        : 'linear-gradient(106deg, rgb(30 30 30) 0%, rgba(136,70,70,1) 50%, rgb(30 30 30) 100%)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {!matchesAvailable ? (
                      <Tooltip
                        title={
                          'Matches will be available when players within your MMR range seed more games'
                        }
                        placement="top"
                      >
                        <InfoIcon
                          sx={{
                            color: '#ec7373',
                            mr: 2,
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={
                          'MMRs are snapshotted for seeded games. So you might be matched against an opponent that has moved out of your MMR range since the seeding'
                        }
                        placement="top"
                      >
                        <InfoIcon
                          sx={{
                            color: '#96ff7f',
                            mr: 1,
                          }}
                        />
                      </Tooltip>
                    )}
                    There {matchesAvailable === 1 ? 'is' : 'are'}{' '}
                    <Typography
                      variant="h6"
                      display="inline"
                      fontWeight={600}
                      color={matchesAvailable ? 'success.main' : 'warning.main'}
                      mx={1}
                    >
                      {matchesAvailable}
                    </Typography>{' '}
                    match{matchesAvailable === 1 ? '' : 'es'} available
                  </Typography> */}
                  <Stack
                    spacing={3}
                    width={'100%'}
                    justifyContent={'center'}
                    alignItems={'center'}
                  >
                    <Stack direction={'row'} alignItems={'center'}>
                      <Box
                        position={'relative'}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Tooltip
                          title={`You can seed a game anytime. The more seeds the better!`}
                          placement="top"
                        >
                          <InfoIcon
                            sx={{
                              color: 'lightgray',
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <FormGroup>
                        <FormControlLabel
                          value="top"
                          control={
                            <Checkbox
                              checked={seedMatch}
                              onChange={(e) => setSeedMatch(e.target.checked)}
                            />
                          }
                          label={'Seed a game'}
                          labelPlacement="start"
                        />
                      </FormGroup>
                    </Stack>
                  </Stack>
                </>
              )}
              <Button
                variant="text"
                color="primary"
                sx={{ alignSelf: 'center' }}
                onClick={() => onViewSearchPoolClick(onlinePlayer.profileId)}
              >
                View Pool Data
              </Button>
            </Stack>
          )}
        </Stack>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={
              mdHeightScreen
                ? {
                    marginTop: 'auto',
                    marginBottom: '100px',
                    justifyContent: 'center',
                  }
                : {
                    position: 'absolute',
                    bottom: '50%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }
            }
          >
            <IconButton
              size="small"
              sx={{
                padding: 0,
                '&:hover': { backgroundColor: 'transparent' },
              }}
              onClick={closePlayMenu}
            >
              <Box className="container zoom_shrink">
                <Box className="close_icon zoom_shrink"></Box>
              </Box>
            </IconButton>
            <CreateGameTooltip
              title={
                soloMultiAsync === 'multiplayer'
                  ? createGameTooltip
                  : soloMultiAsync === 'teams'
                  ? teamGameTooltip
                  : !categorySelections?.length && categorySet === 'none'
                  ? selectCategoryTooltip
                  : null
              }
              placement="top"
            >
              <Box>
                <ValorantButton
                  disabled={disablePlayButton || playClicked}
                  label={
                    soloMultiAsync === 'solo'
                      ? 'START SOLO'
                      : soloMultiAsync === 'multiplayer'
                      ? 'CREATE SNG'
                      : soloMultiAsync === 'teams'
                      ? 'CREATE TEAMS'
                      : (soloMultiAsync === 'async' &&
                          profile.seedMatch === 0) ||
                        seedMatch
                      ? 'SEED MATCH'
                      : 'SEARCH MATCH'
                  }
                  loading={playClicked}
                  loadingLabel={
                    soloMultiAsync === 'solo'
                      ? 'STARTING..'
                      : soloMultiAsync === 'multiplayer'
                      ? 'CREATING..'
                      : soloMultiAsync === 'teams'
                      ? 'CREATING..'
                      : (soloMultiAsync === 'async' &&
                          profile.seedMatch === 0) ||
                        seedMatch
                      ? 'SEEDING..'
                      : 'SEARCHING..'
                  }
                  onClick={() => handlePlayClick(onlinePlayer)}
                />
              </Box>
            </CreateGameTooltip>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
